import { createMachine } from 'xstate';
import * as actions from './actions';
import {
    beginRuleChange,
    finishRuleChange,
    reportRuleChangeFailure,
    rememberSubmission,
    isCurrentRuleChange
} from './submissionActions';

export default function getFormFsm(machineContext) {
    const keepOpenAfterSubmit = machineContext && machineContext.keepOpenAfterSubmit && machineContext.selectedTheme === 'gl2025';
    const machineConfig = {
        ...config,
        states: {
            ...config.states,
            submitted:
                keepOpenAfterSubmit
                    ? {
                          entry: 'raiseSuccess',
                          always: { target: '#Form.edit.ready' }
                      }
                    : {
                          entry: 'raiseSuccess',
                          type: 'final'
                      }
        }
    };
    return createMachine(
        { context: { pendingRuleChanges: [], submissionEvent: null, ...machineContext }, ...machineConfig },
        {
            actions: { ...actions, beginRuleChange, finishRuleChange, reportRuleChangeFailure, rememberSubmission },
            guards: { isCurrentRuleChange }
        }
    );
}

export const config = {
    id: 'Form',
    initial: 'edit',
    states: {
        view: {},
        edit: {
            initial: 'ready',
            on: {
                change: {
                    target: '.dirty',
                    actions: ['optimisticallyMergeChangedRecord', 'mergeChanges']
                },
                changePending: { actions: 'beginRuleChange' },
                changeSuccess: {
                    target: '.dirty',
                    cond: 'isCurrentRuleChange',
                    actions: ['mergeChangedRecord', 'finishRuleChange']
                },
                changeFailure: {
                    target: '.dirty.invalid',
                    cond: 'isCurrentRuleChange',
                    actions: ['finishRuleChange', 'reportRuleChangeFailure']
                },
                restart: {
                    target: '.ready',
                    actions: 'updateContext'
                },
                restartWithChanges: {
                    target: '.dirty',
                    actions: 'updateContext'
                },
                removedHNode: {
                    target: '.dirty',
                    actions: ['beginRuleChange', 'removedHNode']
                }
            },
            states: {
                ready: {},
                dirty: {
                    initial: 'valid',
                    on: {
                        submit: '.submitting'
                    },
                    states: {
                        valid: {},
                        invalid: {},
                        submitting: {
                            initial: 'waitingForChanges',
                            entry: 'rememberSubmission',
                            on: {
                                // Save waits for all outstanding blur rules before validation.
                                changeSuccess: {
                                    cond: 'isCurrentRuleChange',
                                    actions: ['mergeChangedRecord', 'finishRuleChange']
                                },
                                changeFailure: {
                                    target: '#Form.edit.dirty.invalid',
                                    cond: 'isCurrentRuleChange',
                                    actions: ['finishRuleChange', 'reportRuleChangeFailure']
                                }
                            },
                            states: {
                                waitingForChanges: {
                                    always: { target: 'validating', cond: context => context.pendingRuleChanges.length === 0 }
                                },
                                validating: {
                                    on: {
                                        validate_success: [
                                            { target: 'waitingForChanges', cond: context => context.pendingRuleChanges.length > 0 },
                                            {
                                                target: '#Form.edit.dirty.invalid',
                                                cond: (context, event) =>
                                                    Object.keys(event.errors?.field ?? {}).length ||
                                                    event.errors?.form?.length
                                            },
                                            { target: 'updating', cond: context => !context.isNew },
                                            { target: 'creating', cond: context => context.isNew }
                                        ],
                                        validate_failure: '#Form.edit.dirty.invalid'
                                    },
                                    entry: 'validate'
                                },
                                updating: {
                                    on: {
                                        update_success: [
                                            {
                                                target: '#Form.submitted',
                                                cond:  (context, event) => !event.keepOpenAfterSubmit
                                            },
                                            {
                                                target: '#Form.edit.ready',
                                                cond: (context, event) => event.keepOpenAfterSubmit
                                            }
                                        ],
                                        update_failure: '#Form.edit.dirty.invalid'
                                    },
                                    entry: 'update'
                                },
                                creating: {
                                    on: {
                                        create_success: '#Form.submitted',
                                        create_failure: '#Form.edit.dirty.invalid'
                                    },
                                    entry: 'create'
                                }
                            }
                        }
                    }
                }
            }
        },
        submitted: {
            entry: 'raiseSuccess',
            always: { target: 'edit.ready' }
        }
    }
};
